import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Modal, Select, Space, Tag, Typography } from 'antd';
import Drawer from '../../../components/AppDrawer';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isInitialQueryLoading, listQueryDefaults } from '../../../lib/query/listQuery';
import { usePagedListUrlState } from '../../../lib/query/usePagedListUrlState';
import { useUrlListFilter } from '../../../lib/query/useUrlListFilter';
import { DOCUMENT_FILTER_FIELDS } from '../../../routes/filterMaps';
import DataTable from '../../../components/DataTable';
import { ListPage, FilterBar, buildCogActionsColumn } from '../../../components/list';
import Can from '../../permissions/Can';
import { toast } from '../../../lib/toast';
import type { QuoteListItem, QuoteStatus } from '../types';
import { fetchQuotes } from '../api/documents';
import { useDocument } from '../hooks/useDocument';
import { useCreateDocument, useDeleteDocument } from '../hooks/useCreateDocument';
import { DocumentDetailsDrawer } from './DocumentDetailsDrawer';
import { useLeadOptions } from '../../../hooks/options/useLeadOptions';
import { useContactOptions } from '../../../hooks/options/useContactOptions';
import { contactDisplayName } from '../../contacts/contactType';
import LeadViewDrawer from '../../leads/components/LeadViewDrawer';

const { Text } = Typography;

const STATUS_LABELS: Record<QuoteStatus, string> = {
  0: 'Draft',
  1: 'Sent',
  2: 'Accepted',
  3: 'Rejected',
  4: 'Revision Requested',
  5: 'Expired',
  6: 'Cancelled',
};

const STATUS_COLORS: Record<QuoteStatus, string> = {
  0: 'default',
  1: 'blue',
  2: 'green',
  3: 'red',
  4: 'orange',
  5: 'volcano',
  6: 'default',
};

function formatDateTime(s: string | null | undefined): React.ReactNode {
  if (s == null || s === '') return '';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = d.getHours() % 12 || 12;
    const min = String(d.getMinutes()).padStart(2, '0');
    const ampm = d.getHours() < 12 ? 'AM' : 'PM';
    return <span title={`${y}/${m}/${day} ${String(h).padStart(2, '0')}:${min} ${ampm}`}>{y}/{m}/{day}</span>;
  } catch {
    return s ?? '';
  }
}

interface DocumentsFilterValues {
  search?: string;
  status?: QuoteStatus;
}

const defaultDocumentsFilter: DocumentsFilterValues = {};

const DocumentsPage: React.FC = () => {
  const { page, pageSize, setPage, setPageSize } = usePagedListUrlState(10);
  const [filter, setFilter] = useUrlListFilter(defaultDocumentsFilter, DOCUMENT_FILTER_FIELDS);
  const { search, status } = filter;
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createFiles, setCreateFiles] = useState<File[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [createForm] = Form.useForm<{
    leadId: string | null;
    title: string;
    description?: string;
  }>();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const listParams = useMemo(
    () => ({
      pageNumber: page,
      pageSize,
      search,
      status,
    }),
    [page, pageSize, search, status]
  );

  const { data: paged, isLoading, isError: listIsError, error: listError } = useQuery({
    queryKey: ['documents', listParams],
    queryFn: () => fetchQuotes(listParams),
    ...listQueryDefaults,
  });
  const loadingList = isInitialQueryLoading(isLoading, paged);

  useEffect(() => {
    if (listIsError) {
      toast.error(
        listError instanceof Error ? listError.message : 'Failed to load documents. Please try again.',
      );
    }
  }, [listIsError, listError]);

  const { data: selectedQuote, isLoading: loadingQuote } = useDocument(selectedQuoteId);
  const createMutation = useCreateDocument();
  const deleteMutation = useDeleteDocument();
  const queryClient = useQueryClient();
  const { data: contacts = [] } = useContactOptions();
  const { data: leads = [] } = useLeadOptions(
    { contactId: selectedContactId ?? undefined },
    selectedContactId != null,
  );

  const contactOptions = useMemo(
    () =>
      contacts.map((c) => ({
        value: c.id,
        label: contactDisplayName(c) || c.email || c.id,
      })),
    [contacts]
  );

  const leadOptions = useMemo(
    () =>
      leads.map((l) => ({
        value: l.id,
        label: l.label,
      })),
    [leads]
  );

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    } else {
      setPage(newPage);
    }
  }, [pageSize, setPage, setPageSize]);

  const handleResetFilters = useCallback(() => {
    setFilter({});
  }, [setFilter]);

  const handleCreateQuote = async () => {
    try {
      const values = await createForm.validateFields();

      if (createFiles.length === 0) {
        Modal.error({
          title: 'Files required',
          content: 'Please select at least one PDF file.',
        });
        return;
      }

      const result = await createMutation.mutateAsync({
        leadId: values.leadId ?? null,
        title: values.title,
        description: values.description,
        files: createFiles,
      });

      setCreateModalOpen(false);
      setCreateFiles([]);
      setSelectedContactId(null);
      createForm.resetFields();
      setSelectedQuoteId(result.id);
      toast.success('Document created successfully.');
    } catch {
      // form validation errors are handled by antd
    }
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
    setCreateFiles([]);
    setSelectedContactId(null);
    createForm.resetFields();
  };

  const handleDelete = useCallback(async (record: QuoteListItem) => {
    await deleteMutation.mutateAsync(record.id);
    if (selectedQuoteId === record.id) {
      setSelectedQuoteId(null);
    }
  }, [deleteMutation, selectedQuoteId]);

  const columns: ColumnsType<QuoteListItem> = useMemo(
    () => [
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        render: (_: string, record: QuoteListItem) => (
          <Typography.Link onClick={() => setSelectedQuoteId(record.id)}>
            {record.title}
          </Typography.Link>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (s: QuoteStatus) => (
          <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s]}</Tag>
        ),
      },
      {
        title: 'Lead',
        dataIndex: 'leadTitle',
        key: 'leadTitle',
        render: (_: string | null, record: QuoteListItem) => (
            record.leadId ? (
              <Typography.Link
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedLeadId(record.leadId);
                }}
              >
                {record.leadTitle ?? 'View'}
              </Typography.Link>
            ) : (
              <Typography.Text type="secondary">—</Typography.Text>
            )
          ),
      },
      {
        title: 'Lead owner',
        dataIndex: 'leadAssigneeDisplayName',
        key: 'leadAssigneeDisplayName',
        render: (v: string | null) => v ?? '—',
      },
      {
        title: 'Created',
        dataIndex: 'createdAtUtc',
        key: 'createdAtUtc',
        render: (v: string) => formatDateTime(v),
      },
      {
        title: 'Sent',
        dataIndex: 'sentAtUtc',
        key: 'sentAtUtc',
        render: (v: string | null) => formatDateTime(v),
      },
      {
        title: 'Sender',
        dataIndex: 'sentByDisplayName',
        key: 'sentByDisplayName',
        render: (v: string | null, record: QuoteListItem) =>
          v ?? record.createdByDisplayName ?? '—',
      },
      {
        title: 'Accepted expiry',
        dataIndex: 'acceptedExpiryDate',
        key: 'acceptedExpiryDate',
        render: (v: string | null) => formatDateTime(v),
      },
      buildCogActionsColumn<QuoteListItem>({
        onView: (record) => setSelectedQuoteId(record.id),
        onEdit: (record) => setSelectedQuoteId(record.id),
        editVisible: (record) => record.status === 0,
        onDelete: (record) => {
          void handleDelete(record);
        },
        deleteConfirm: () => ({
          title: 'Delete document',
          content: 'Are you sure you want to delete this document?',
        }),
        rowNumber: (_record, index) =>
          paged ? (paged.pageNumber - 1) * paged.pageSize + index + 1 : index + 1,
      }),
    ],
    [handleDelete, paged],
  );

  return (
    <>
      <ListPage
        title="Documents"
        subtitle="Create, send, and track quotes and customer documents."
        actions={(
          <Can permission="quotes.create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalOpen(true)}
            >
              Add document
            </Button>
          </Can>
        )}
        filters={(
          <FilterBar
            onReset={search !== undefined || status !== undefined ? handleResetFilters : undefined}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Search</span>
              <Input.Search
                allowClear
                style={{ width: 240 }}
                onSearch={(value) => {
                  setPage(1);
                  setFilter((prev) => ({ ...prev, search: value || undefined }));
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)', lineHeight: '16px' }}>Status</span>
              <Select<QuoteStatus | undefined>
                allowClear
                style={{ width: 160 }}
                value={status}
                onChange={(value) => {
                  setPage(1);
                  setFilter((prev) => ({ ...prev, status: value }));
                }}
                options={[
                  { value: 0 as QuoteStatus, label: 'Draft' },
                  { value: 1 as QuoteStatus, label: 'Sent' },
                  { value: 2 as QuoteStatus, label: 'Accepted' },
                  { value: 3 as QuoteStatus, label: 'Rejected' },
                  { value: 4 as QuoteStatus, label: 'Revision Requested' },
                  { value: 5 as QuoteStatus, label: 'Expired' },
                  { value: 6 as QuoteStatus, label: 'Cancelled' },
                ]}
              />
            </div>
          </FilterBar>
        )}
      >
        <DataTable<QuoteListItem>
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={loadingList}
          rowKey="id"
          bordered
          striped
          pagination={
            paged
              ? {
                  current: paged.pageNumber,
                  pageSize: paged.pageSize,
                  total: paged.totalCount,
                  onChange: handlePaginationChange,
                }
              : undefined
          }
          emptyText="No documents yet."
        />
      </ListPage>

      <Drawer
        title="Add document"
        placement="right"
        size="large"
        open={createModalOpen}
        onClose={handleCreateModalClose}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={handleCreateModalClose} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" loading={createMutation.isPending} onClick={handleCreateQuote}>
              Save
            </Button>
          </div>
        }
      >
        <div className="cp-form-section">
          <Form layout="vertical" form={createForm}>
            <Form.Item
              name="contactId"
              label="Contact"
              rules={[{ required: true, message: 'Select a contact' }]}
            >
            <Select
              placeholder="Select contact"
              options={contactOptions}
              showSearch
              optionFilterProp="label"
              onChange={(value) => {
                setSelectedContactId(value ?? null);
                createForm.setFieldValue('leadId', undefined);
              }}
            />
          </Form.Item>
          <Form.Item
            name="leadId"
            label="Lead"
          >
            <Select
              placeholder={selectedContactId ? 'Select lead' : 'Select a contact first'}
              options={leadOptions}
              disabled={!selectedContactId}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Title is required' }]}
          >
            <Input placeholder="Document title" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional notes" />
          </Form.Item>
          <Form.Item label="Files" required>
            <Space direction="vertical" style={{ width: '100%' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  setCreateFiles(files);
                }}
              />
              <Button onClick={() => fileInputRef.current?.click()}>
                Choose PDF files
              </Button>
              <Text type={createFiles.length ? undefined : 'secondary'}>
                {createFiles.length
                  ? createFiles.map((f) => f.name).join(', ')
                  : 'No files selected.'}
              </Text>
            </Space>
          </Form.Item>
          {createMutation.isError && (
            <Text type="danger">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create document.'}
            </Text>
          )}
          </Form>
        </div>
      </Drawer>

      <DocumentDetailsDrawer
        open={selectedQuoteId != null}
        onClose={() => setSelectedQuoteId(null)}
        quote={selectedQuote}
        loading={loadingQuote}
        onUpdated={() => {
          if (selectedQuoteId) {
            queryClient.invalidateQueries({ queryKey: ['documents', selectedQuoteId] });
          }
          void queryClient.invalidateQueries({ queryKey: ['documents'] });
        }}
      />

      <LeadViewDrawer
        open={selectedLeadId != null}
        onClose={() => setSelectedLeadId(null)}
        leadId={selectedLeadId}
      />
    </>
  );
};

export default DocumentsPage;
